import React, { useEffect, useState } from "react";
import api from "../../api";
import { themeClass } from "../../components/ThemeChip";
import { BookOpen, MessagesSquare, Sparkles, NotebookPen, Eye } from "lucide-react";

export default function AdminPreview() {
  const [themes, setThemes] = useState([]);
  const [themeSlug, setThemeSlug] = useState("");
  const [week, setWeek] = useState(1);
  const [ageGroup, setAgeGroup] = useState("9-13");
  const [data, setData] = useState(null);
  const [personalize, setPersonalize] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/themes").then((r) => {
      const pubs = r.data.filter((t) => t.status === "published");
      setThemes(pubs);
      if (pubs.length) setThemeSlug(pubs[0].slug);
    });
  }, []);

  const load = async () => {
    if (!themeSlug) return;
    setBusy(true);
    try {
      const r = await api.get("/admin/preview/week", {
        params: { theme_slug: themeSlug, week, age_group: ageGroup,
                  personalize: personalize ? "true" : "false" },
      });
      setData(r.data);
    } catch (e) { setData(null); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [themeSlug, week, ageGroup, personalize]);

  const t = data?.theme;
  const story = data?.personalized?.story || data?.story;
  const qs = data?.personalized?.questions || (data?.questions || []).map((q) => ({ id: q.id, text: q.question_text, hint: q.facilitation_hint }));
  const activity = data?.personalized?.activity || (data?.activity ? {
    name: data.activity.name, instructions_md: data.activity.instructions_md, estimated_minutes: data.activity.estimated_minutes
  } : null);

  return (
    <div data-testid="admin-preview-screen">
      <h1 className="font-display text-4xl mb-2">Preview</h1>
      <p className="text-muted-foreground mb-6">Render exactly as a parent sees it.</p>

      <div className="card-soft p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Theme</label>
          <select value={themeSlug} onChange={(e) => setThemeSlug(e.target.value)}
                   className="field py-2 mt-1 text-sm" data-testid="admin-preview-theme">
            {themes.map((th) => <option key={th.slug} value={th.slug}>{th.title}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Week</label>
          <input type="number" min={1} max={12} value={week} onChange={(e) => setWeek(parseInt(e.target.value, 10) || 1)}
                  className="field py-2 mt-1 text-sm" data-testid="admin-preview-week"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Age group</label>
          <select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}
                   className="field py-2 mt-1 text-sm" data-testid="admin-preview-age">
            <option>4-8</option><option>9-13</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={personalize} onChange={(e) => setPersonalize(e.target.checked)}
                 data-testid="admin-preview-personalize"/>
          Personalize with AI
        </label>
      </div>

      <div className="sticky top-16 lg:top-0 z-10 bg-red-500/90 text-white text-center py-1.5 rounded-t-2xl text-xs uppercase tracking-widest mb-0"
           data-testid="admin-preview-ribbon">
        Preview mode. not visible to parents
      </div>
      <div className={`card-elevated rounded-t-none -mt-px p-7 ${themeClass(t?.color_token)}`}>
        {!data && <div className="text-muted-foreground">Loading…</div>}
        {data && (
          <>
            <div className="text-xs uppercase tracking-widest theme-text mb-1">{t?.title} · Week {data.week?.week_number}</div>
            <h2 className="font-display text-3xl mb-6">{data.week?.title}</h2>

            <section className="mb-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2">
                <BookOpen className="w-3 h-3"/> Step 1 · Story
              </div>
              <h3 className="font-display text-2xl mb-3">{story?.title}</h3>
              <article className="reader whitespace-pre-wrap">{story?.content_md}</article>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2">
                <MessagesSquare className="w-3 h-3"/> Step 2 · Conversation
              </div>
              <ol className="space-y-3">
                {qs.map((q, i) => (
                  <li key={q.id || i} className="card-soft p-4">
                    <span className="font-display text-xl">{q.text}</span>
                    {q.hint && <div className="text-xs text-muted-foreground mt-1 italic">{q.hint}</div>}
                  </li>
                ))}
              </ol>
            </section>

            <section className="mb-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2">
                <Sparkles className="w-3 h-3"/> Step 3 · Activity
              </div>
              <h3 className="font-display text-2xl mb-1">{activity?.name}</h3>
              <div className="text-xs text-muted-foreground mb-2">{activity?.estimated_minutes} min</div>
              <article className="reader text-[1.1rem] whitespace-pre-wrap">{activity?.instructions_md}</article>
            </section>

            <section>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-2">
                <NotebookPen className="w-3 h-3"/> Step 4 · Reflection
              </div>
              <ol className="space-y-2">
                {(data.reflection_questions || []).map((r) => (
                  <li key={r.id} className="card-soft p-3 font-display text-lg">{r.question_text}</li>
                ))}
              </ol>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
